import { supabase } from '@/lib/supabase/client';

export type RentalInventoryStatus = 'disponivel' | 'alugado' | 'reservado' | 'manutencao';
export type RentalStatus = 'pendente' | 'ativo' | 'concluido' | 'atrasado' | 'cancelado';

export interface RentalCategory {
  id: string;
  name: string;
}

export interface RentalInventoryItem {
  id: string;
  name: string;
  description: string | null;
  fabric: string | null;
  daily_rate: number;
  replacement_value: number;
  sizes: string[];
  status: RentalInventoryStatus;
  image_url: string | null;
  created_at: string;
  category?: string | null;
  categories?: RentalCategory[];
  images?: Array<{ id: string; image_url: string; display_order: number }>;
  rentals?: Rental[];
}

export interface Rental {
  id: string;
  client_id: string;
  inventory_item_id: string;
  pickup_date: string;
  fitting_date?: string | null;
  return_date: string;
  total_price: number;
  cleaning_fee: number;
  status: RentalStatus;
  notes: string | null;
  created_at: string;
  inventory_item?: RentalInventoryItem;
  client?: {
    id: string;
    name: string;
    email: string;
  };
}

export const rentalService = {
  // Category Methods
  async getCategories(): Promise<RentalCategory[]> {
    const { data, error } = await supabase
      .from('rental_categories')
      .select('*')
      .order('name', { ascending: true });
    
    if (error) throw error;
    return data || [];
  },

  // Inventory Methods
  async getInventory(): Promise<RentalInventoryItem[]> {
    const { data, error } = await supabase
      .from('rental_inventory')
      .select(`
        *,
        pivot:rental_inventory_categories(
          category:rental_categories(*)
        ),
        rentals:rentals(
          *,
          client:clients(*)
        )
      `)
      .order('name', { ascending: true });
    
    if (error) throw error;
    
    // Flatten categories and pass rentals
    return (data || []).map(item => ({
      ...item,
      categories: item.pivot?.map((p: any) => p.category) || [],
      rentals: item.rentals || []
    }));
  },

  async getInventoryItemById(id: string): Promise<RentalInventoryItem | null> {
    const { data, error } = await supabase
      .from('rental_inventory')
      .select(`
        *,
        pivot:rental_inventory_categories(
          category:rental_categories(*)
        ),
        rentals:rentals(
          *,
          client:clients(*)
        )
      `)
      .eq('id', id)
      .maybeSingle();
    
    if (error) throw error;
    if (!data) return null;

    // Fetch images
    const { data: imagesData } = await supabase
      .from('rental_item_images')
      .select('*')
      .eq('item_id', id)
      .order('display_order', { ascending: true });

    return {
      ...data,
      categories: data.pivot?.map((p: any) => p.category) || [],
      images: imagesData || [],
      rentals: data.rentals || []
    };
  },

  async createInventoryItem(
    item: Partial<RentalInventoryItem>, 
    categoryIds: string[],
    imageUrls: string[] = []
  ): Promise<RentalInventoryItem> {
    // 1. Criar a peça
    const { data: newItem, error: itemError } = await supabase
      .from('rental_inventory')
      .insert([item])
      .select()
      .single();
    
    if (itemError) throw itemError;

    // 2. Vincular categorias
    if (categoryIds.length > 0) {
      const pivotData = categoryIds.map(catId => ({
        inventory_id: newItem.id,
        category_id: catId
      }));
      const { error: pivotError } = await supabase
        .from('rental_inventory_categories')
        .insert(pivotData);
      
      if (pivotError) throw pivotError;
    }
    
    // 3. Salvar fotos adicionais
    if (imageUrls.length > 0) {
      const imagesData = imageUrls.map((url, index) => ({
        item_id: newItem.id,
        image_url: url,
        display_order: index
      }));
      const { error: imagesError } = await supabase
        .from('rental_item_images')
        .insert(imagesData);
      
      if (imagesError) throw imagesError;
    }

    return newItem;
  },

  async createCategory(name: string): Promise<RentalCategory> {
    const { data, error } = await supabase
      .from('rental_categories')
      .insert([{ name }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateInventoryItem(
    id: string, 
    updates: Partial<RentalInventoryItem>,
    categoryIds?: string[],
    imageUrls?: string[]
  ): Promise<void> {
    // 1. Update basic info
    const { error } = await supabase
      .from('rental_inventory')
      .update(updates)
      .eq('id', id);
    
    if (error) throw error;

    // 2. Update categories if provided
    if (categoryIds) {
      // Delete existing
      await supabase.from('rental_inventory_categories').delete().eq('inventory_id', id);
      
      // Insert new
      if (categoryIds.length > 0) {
        const pivotData = categoryIds.map(catId => ({
          inventory_id: id,
          category_id: catId
        }));
        await supabase.from('rental_inventory_categories').insert(pivotData);
      }
    }

    // 3. Update images if provided
    if (imageUrls) {
      // Delete existing
      await supabase.from('rental_item_images').delete().eq('item_id', id);
      
      // Insert new
      if (imageUrls.length > 0) {
        const imagesData = imageUrls.map((url, index) => ({
          item_id: id,
          image_url: url,
          display_order: index
        }));
        await supabase.from('rental_item_images').insert(imagesData);
      }
    }
  },

  // Rental Transaction Methods
  async getRentals(): Promise<Rental[]> {
    const { data, error } = await supabase
      .from('rentals')
      .select(`
        *,
        inventory_item:rental_inventory(*),
        client:clients(*)
      `)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  async createRental(rentalData: Partial<Rental>): Promise<Rental> {
    const { data, error } = await supabase
      .from('rentals')
      .insert([rentalData])
      .select()
      .single();
    
    if (error) throw error;

    // Update item status to 'reservado' or 'alugado' based on pickup date
    if (rentalData.inventory_item_id) {
      const todayStr = new Date().toISOString().split('T')[0];
      const pickupStr = rentalData.pickup_date ? rentalData.pickup_date.split('T')[0] : todayStr;
      const isTodayOrPast = pickupStr <= todayStr;
      const itemStatus = isTodayOrPast ? 'alugado' : 'reservado';
      await this.updateInventoryItem(rentalData.inventory_item_id, { status: itemStatus });
    }

    return data;
  }
};
